import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Settings,
  User,
  Moon,
  Sun,
  HelpCircle,
  Volume2,
  VolumeX,
  Info,
  LogOut,
  ChevronRight,
  LogIn,
  Bell,
  BellRing,
  Share2,
  Trash2,
  AlertTriangle,
} from 'lucide-react';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from './ui/dropdown-menu';
import { Switch } from './ui/switch';
import soundEffects from '../utils/soundEffects';
import { isUserLoggedIn, logoutUser, getCurrentUser, deleteUserAccount } from '../utils/auth';
import { supabase } from '../lib/supabase';
import { toast } from 'sonner';
import { NotificationsPanel } from './NotificationsPanel';
import {
  subscribeToPushNotifications,
  unsubscribeFromPushNotifications,
  isPushSubscribed,
  registerServiceWorker
} from '../utils/pushNotifications';

export const SettingsMenu = () => {
  const navigate = useNavigate();
  const [darkMode, setDarkMode] = useState(false);
  const [soundEnabled, setSoundEnabled] = useState(true);
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [currentUser, setCurrentUser] = useState(null);
  const [showNotifications, setShowNotifications] = useState(false);
  const [unreadCount, setUnreadCount] = useState(0);
  const [pushEnabled, setPushEnabled] = useState(false);
  const [pushLoading, setPushLoading] = useState(false);
  const [showDeleteAlert, setShowDeleteAlert] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  // Check authentication status
  useEffect(() => {
    const checkAuth = async () => {
      const loggedIn = await isUserLoggedIn();
      setIsLoggedIn(loggedIn);
      if (loggedIn) {
        const user = await getCurrentUser();
        setCurrentUser(user);

        // Verificar estado de notificaciones push
        await registerServiceWorker();
        const subscribed = await isPushSubscribed();
        setPushEnabled(subscribed);
      }
    };
    checkAuth();
  }, []);

  // Fetch unread notifications count
  useEffect(() => {
    if (isLoggedIn && currentUser) {
      const fetchUnread = async () => {
        const { count, error } = await supabase
          .from('notifications')
          .select('*', { count: 'exact', head: true })
          .eq('user_id', currentUser.id)
          .eq('read', false);

        if (!error) setUnreadCount(count || 0);
      };

      fetchUnread();

      // Suscribirse a cambios en tiempo real
      const subscription = supabase
        .channel('notifications_changes')
        .on('postgres_changes', {
          event: '*',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${currentUser.id}`
        }, (payload) => {
          fetchUnread();
        })
        .subscribe();

      return () => {
        subscription.unsubscribe();
      };
    }
  }, [isLoggedIn, currentUser]);

  const handleLogout = async () => {
    const result = await logoutUser();
    if (result.success) {
      toast.success('Sesión cerrada exitosamente');
      setIsLoggedIn(false);
      setCurrentUser(null);
      navigate('/');
    } else {
      toast.error('Error al cerrar sesión');
    }
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Tetris Bíblico',
          text: '¡Mira esta increíble aplicación de Tetris Bíblico! Construye tu fe, bloque a bloque.',
          url: window.location.origin,
        });
      } catch (error) {
        if (error.name !== 'AbortError') {
          console.error('Error al compartir:', error);
          toast.error('Error al abrir las opciones de compartir');
        }
      }
    } else {
      // Fallback: Copy to clipboard
      try {
        await navigator.clipboard.writeText(`¡Mira esta increíble aplicación de Tetris Bíblico! ${window.location.origin}`);
        toast.success('Enlace copiado al portapapeles');
      } catch (err) {
        toast.info('La función de compartir no está disponible');
      }
    }
  };

  const handleDeleteAccount = async () => {
    if (!currentUser) return;

    setIsDeleting(true);
    try {
      const result = await deleteUserAccount(currentUser.id);
      if (result.success) {
        toast.success('Cuenta eliminada correctamente');
        setIsLoggedIn(false);
        setCurrentUser(null);
        navigate('/');
      } else {
        toast.error(result.error || 'Error al eliminar la cuenta');
      }
    } catch (error) {
      console.error('Error deleting account:', error);
      toast.error('Error inesperado al eliminar la cuenta');
    } finally {
      setIsDeleting(false);
      setShowDeleteAlert(false);
    }
  };

  // Check for dark mode preference on mount
  useEffect(() => {
    const isDark = document.documentElement.classList.contains('dark');
    setDarkMode(isDark);
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    const newDarkMode = !darkMode;
    setDarkMode(newDarkMode);

    if (newDarkMode) {
      document.documentElement.classList.add('dark');
      localStorage.setItem('theme', 'dark');
    } else {
      document.documentElement.classList.remove('dark');
      localStorage.setItem('theme', 'light');
    }
  };

  // Toggle sound
  const toggleSound = () => {
    const newSoundEnabled = !soundEnabled;
    setSoundEnabled(newSoundEnabled);
    soundEffects.setEnabled(newSoundEnabled);
    localStorage.setItem('tetris_sound_enabled', JSON.stringify(newSoundEnabled));
  };

  // Toggle push notifications
  const togglePushNotifications = async () => {
    if (!currentUser) {
      toast.error('Debes iniciar sesión para activar notificaciones');
      return;
    }

    setPushLoading(true);
    try {
      if (pushEnabled) {
        await unsubscribeFromPushNotifications(currentUser.id);
        setPushEnabled(false);
        toast.success('Notificaciones push desactivadas');
      } else {
        await subscribeToPushNotifications(currentUser.id);
        setPushEnabled(true);
        toast.success('¡Notificaciones push activadas! Recibirás versículos diarios y alertas de logros.');
      }
    } catch (error) {
      console.error('Error al gestionar notificaciones push:', error);
      const errorMessage = error.message || 'Error al configurar notificaciones. Asegúrate de dar permisos en tu navegador.';
      toast.error(errorMessage);
    } finally {
      setPushLoading(false);
    }
  };

  // Load sound preference
  useEffect(() => {
    const savedSound = localStorage.getItem('tetris_sound_enabled');
    if (savedSound !== null) {
      try {
        const isEnabled = JSON.parse(savedSound);
        setSoundEnabled(isEnabled);
        soundEffects.setEnabled(isEnabled);
      } catch (e) {
        console.error('Error parsing sound preference:', e);
      }
    }
  }, []);


  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <button className="p-2 hover:bg-secondary rounded-lg interactive relative">
          <Settings className="w-6 h-6" />
          {unreadCount > 0 && (
            <span className="absolute top-1.5 right-1.5 w-2.5 h-2.5 bg-red-500 border-2 border-background rounded-full shadow-sm animate-pulse"></span>
          )}
        </button>
      </DropdownMenuTrigger>
      <DropdownMenuContent align="end" className="w-64 p-2 shadow-2xl border-2 border-primary/10 bg-card/95 backdrop-blur-md rounded-2xl">
        <DropdownMenuLabel className="text-lg font-bold px-3 py-2">Ajustes</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {/* User Info (if logged in) */}
        {isLoggedIn && currentUser && (
          <div className="mx-1 my-2 p-3 bg-secondary/50 rounded-xl border border-secondary">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-primary flex items-center justify-center font-bold text-primary-foreground">
                {currentUser.user_metadata?.full_name?.charAt(0) || currentUser.email?.charAt(0) || 'U'}
              </div>
              <div className="flex-1 min-w-0">
                <p className="font-bold text-sm truncate">
                  {currentUser.user_metadata?.full_name || currentUser.email.split('@')[0]}
                </p>
                <p className="text-[10px] text-muted-foreground truncate">{currentUser.email}</p>
              </div>
            </div>
          </div>
        )}

        {isLoggedIn && (
          <DropdownMenuItem
            onClick={() => navigate('/profile', { state: { editMode: true } })}
            className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
          >
            <div className="w-8 h-8 rounded-lg bg-blue-100 flex items-center justify-center">
              <User className="w-4 h-4 text-blue-600" />
            </div>
            <span className="font-semibold text-sm">Editar Perfil</span>
            <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
          </DropdownMenuItem>
        )}

        {/* Notificaciones */}
        {isLoggedIn && (
          <DropdownMenuItem
            onClick={() => setShowNotifications(true)}
            className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
          >
            <div className="w-8 h-8 rounded-lg bg-orange-100 flex items-center justify-center relative shadow-sm ring-1 ring-orange-200">
              <Bell className="w-4 h-4 text-orange-600" />
              {unreadCount > 0 && (
                <span className="absolute -top-1 -right-1 w-3 h-3 bg-red-500 border-2 border-card rounded-full shadow-lg"></span>
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm">Notificaciones</span>
              <span className="text-[10px] text-muted-foreground">
                {unreadCount > 0 ? `${unreadCount} nuevas` : 'Ver todas'}
              </span>
            </div>
            <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
          </DropdownMenuItem>
        )}

        {/* Dark Mode Toggle */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            toggleDarkMode();
          }}
          className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            {darkMode ? (
              <Sun className="w-4 h-4 text-indigo-600" />
            ) : (
              <Moon className="w-4 h-4 text-gray-600" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">Modo Oscuro</span>
            <span className="text-[10px] text-muted-foreground">{darkMode ? 'Activado' : 'Desactivado'}</span>
          </div>
          <Switch
            checked={darkMode}
            onCheckedChange={toggleDarkMode}
            className="ml-auto"
            onClick={(e) => e.stopPropagation()}
          />
        </DropdownMenuItem>

        {/* Sound Toggle */}
        <DropdownMenuItem
          onClick={(e) => {
            e.preventDefault();
            toggleSound();
          }}
          className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
        >
          <div className="w-8 h-8 rounded-lg bg-green-100 flex items-center justify-center">
            {soundEnabled ? (
              <Volume2 className="w-4 h-4 text-green-600" />
            ) : (
              <VolumeX className="w-4 h-4 text-gray-600" />
            )}
          </div>
          <div className="flex flex-col">
            <span className="font-semibold text-sm">Sonido</span>
            <span className="text-[10px] text-muted-foreground">{soundEnabled ? 'Activado' : 'Desactivado'}</span>
          </div>
          <Switch
            checked={soundEnabled}
            onCheckedChange={toggleSound}
            className="ml-auto"
            onClick={(e) => e.stopPropagation()}
          />
        </DropdownMenuItem>

        {/* Push Notifications Toggle */}
        {isLoggedIn && (
          <DropdownMenuItem
            onClick={(e) => {
              e.preventDefault();
              if (!pushLoading) togglePushNotifications();
            }}
            className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
          >
            <div className="w-8 h-8 rounded-lg bg-purple-100 flex items-center justify-center">
              {pushEnabled ? (
                <BellRing className="w-4 h-4 text-purple-600" />
              ) : (
                <Bell className="w-4 h-4 text-gray-600" />
              )}
            </div>
            <div className="flex flex-col">
              <span className="font-semibold text-sm">Versículo Diario</span>
              <span className="text-[10px] text-muted-foreground">
                {pushLoading ? 'Cargando...' : pushEnabled ? 'Activo' : 'Recibe versos cada mañana'}
              </span>
            </div>
            <Switch
              checked={pushEnabled}
              onCheckedChange={togglePushNotifications}
              className="ml-auto"
              disabled={pushLoading}
              onClick={(e) => e.stopPropagation()}
            />
          </DropdownMenuItem>
        )}

        <DropdownMenuSeparator />

        <DropdownMenuItem
          onClick={() => navigate('/support')}
          className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
        >
          <div className="w-8 h-8 rounded-lg bg-indigo-100 flex items-center justify-center">
            <HelpCircle className="w-4 h-4 text-indigo-600" />
          </div>
          <span className="font-semibold text-sm">Soporte</span>
          <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={() => navigate('/about')}
          className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
        >
          <div className="w-8 h-8 rounded-lg bg-teal-100 flex items-center justify-center">
            <Info className="w-4 h-4 text-teal-600" />
          </div>
          <span className="font-semibold text-sm">Acerca de</span>
          <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
        </DropdownMenuItem>

        <DropdownMenuItem
          onClick={handleShare}
          className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-secondary focus:bg-secondary transition-colors m-1"
        >
          <div className="w-8 h-8 rounded-lg bg-pink-100 flex items-center justify-center">
            <Share2 className="w-4 h-4 text-pink-600" />
          </div>
          <span className="font-semibold text-sm">Compartir App</span>
          <ChevronRight className="w-4 h-4 ml-auto text-muted-foreground" />
        </DropdownMenuItem>

        <DropdownMenuSeparator />

        {isLoggedIn ? (
          <>
            <DropdownMenuItem
              onClick={handleLogout}
              className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-destructive/10 focus:bg-destructive/10 text-destructive transition-colors m-1"
            >
              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center">
                <LogOut className="w-4 h-4" />
              </div>
              <span className="font-bold text-sm">Cerrar Sesión</span>
            </DropdownMenuItem>

            <DropdownMenuSeparator />

            <DropdownMenuItem
              onClick={(e) => {
                e.preventDefault();
                setShowDeleteAlert(true);
              }}
              className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-destructive focus:bg-destructive text-destructive hover:text-white focus:text-white transition-colors m-1"
            >
              <div className="w-8 h-8 rounded-lg bg-destructive/10 flex items-center justify-center group-hover:bg-white/20">
                <Trash2 className="w-4 h-4" />
              </div>
              <span className="font-bold text-sm">Eliminar Cuenta</span>
            </DropdownMenuItem>
          </>
        ) : (
          <DropdownMenuItem
            onClick={() => navigate('/login')}
            className="flex items-center gap-3 px-3 py-3 rounded-xl cursor-pointer hover:bg-primary/20 focus:bg-primary/20 transition-colors m-1"
          >
            <div className="w-8 h-8 rounded-lg bg-primary/20 flex items-center justify-center">
              <LogIn className="w-4 h-4 text-primary" />
            </div>
            <span className="font-bold text-sm text-primary">Iniciar Sesión</span>
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>

      <NotificationsPanel
        isOpen={showNotifications}
        onClose={() => setShowNotifications(false)}
      />

      <AlertDialog open={showDeleteAlert} onOpenChange={setShowDeleteAlert}>
        <AlertDialogContent className="bg-card border-2 border-destructive/20 rounded-2xl max-w-[90vw] w-96">
          <AlertDialogHeader>
            <div className="mx-auto w-12 h-12 rounded-full bg-destructive/10 flex items-center justify-center mb-2">
              <AlertTriangle className="w-6 h-6 text-destructive" />
            </div>
            <AlertDialogTitle className="text-center text-xl">¿Estás seguro?</AlertDialogTitle>
            <AlertDialogDescription className="text-center text-muted-foreground">
              Esta acción no se puede deshacer. Tu cuenta, progreso, logros y estadísticas serán eliminados permanentemente de nuestros servidores.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter className="sm:justify-center gap-2 mt-4">
            <AlertDialogCancel disabled={isDeleting} className="rounded-xl">Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={(e) => {
                e.preventDefault();
                handleDeleteAccount();
              }}
              disabled={isDeleting}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90 rounded-xl"
            >
              {isDeleting ? 'Eliminando...' : 'Sí, eliminar mi cuenta'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </DropdownMenu >
  );
};
